import { AnimatePresence, motion, useReducedMotion, type Transition } from 'framer-motion';
import { CSSProperties, ReactNode } from 'react';

type AnimatedContentProps = {
  contentKey: string;
  children: ReactNode;
  style?: CSSProperties;
  /** AnimatePresence mode; `wait` suits route changes, `sync` suits in-page tab switches. */
  presenceMode?: 'wait' | 'sync' | 'popLayout';
  /** Drop the outgoing panel immediately instead of playing an exit animation. */
  instantExit?: boolean;
};

const transition: Transition = {
  duration: 0.22,
  ease: [0.4, 0, 0.2, 1],
};

export const AnimatedContent = ({
  contentKey,
  children,
  style,
  presenceMode = 'wait',
  instantExit = false,
}: AnimatedContentProps) => {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return <div style={{ height: '100%', width: '100%', ...style }}>{children}</div>;
  }

  return (
    <AnimatePresence mode={presenceMode} initial={false}>
      <motion.div
        key={contentKey}
        initial={{ opacity: 0, y: instantExit ? 0 : 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{
          opacity: 0,
          y: instantExit ? 0 : -10,
          transition: instantExit ? { duration: 0 } : transition,
        }}
        transition={transition}
        style={{ height: '100%', width: '100%', ...style }}>
        {children}
      </motion.div>
    </AnimatePresence>
  );
};
